import React, { useState, useEffect } from 'react';
import { VapiWidget } from '@vapi-ai/client-sdk-react';
import { api } from '@/api/apiClient';
import { EnrollmentCartProvider } from '@/components/enrollment/EnrollmentCartContext';
import Sidebar from '@/components/layout/Sidebar';
import TopBar from '@/components/layout/TopBar';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { usePageContext } from '@/hooks/usePageContext';
import { useTheme } from '@/hooks/useTheme';

// Pages that don't need authentication (public landing page)
const publicPages = new Set(['Welcome', 'Login', 'Invite', 'CompanySignup', 'RoleSelection', 'EmployeeOnboarding', 'InviteCodeEntry', 'me', 'hr', 'why', 'proposal/:token']);

const normalizeUserRole = (user) => {
  const role = user?.user_role || user?.role || '';
  return role.toLowerCase();
};

const isSystemAdmin = (user) => normalizeUserRole(user) === 'admin';

const getDefaultPageForRole = (userRole) => {
  const defaults = {
    admin: 'AdminDashboard',
    sub_admin: 'EmployerDashboard',
    employer_hr: 'EmployerDashboard',
    employee: 'EmployeeDashboard'
  };
  return defaults[userRole] || 'EmployeeDashboard';
};

const isPageAllowedForRole = (userRole, pageName) => {
  // Public pages are handled earlier.
  if (!userRole || !pageName) return false;

  // Home is a safe redirect target for all authed roles.
  if (pageName === 'Home') return true;

  // Allow the role's default dashboard.
  if (pageName === getDefaultPageForRole(userRole)) return true;

  // Dynamic detail routes
  if (pageName === 'ClaimDetail/:claimId') {
    return userRole === 'admin' || userRole === 'sub_admin' || userRole === 'employer_hr';
  }
  if (pageName === 'EmployeeDetail/:employeeId') {
    return userRole === 'admin' || userRole === 'sub_admin' || userRole === 'employer_hr';
  }

  const allowed = {
    employee: new Set([
      'EmployeeDashboard',
      'BenefitsCatalog',
      'BenefitDetail',
      'MyBenefits',
      'Enrollment',
      'EnrollmentIntro',
      'EnrollmentReview',
      'EnrollmentSign',
      'EmployeeProfile',
      'Claims',
      'FlexOptimization',
      'FamilyMembers',
      'TwoFactorAuth',
      'Help'
    ]),
    employer_hr: new Set([
      'EmployerDashboard',
      'EmployerEmployees',
      'EmployerEnrollments',
      'ClaimsManagement',
      'QualifyingLifeEvents',
      'PayrollReports',
      'DemographicReports',
      'TaxSavingsReports',
      'PenetrationReports',
      'CensusUpload',
      'ExportCensus',
      'BenefitsManagement',
      'Categories',
      'EnrollmentPeriods',
      'TwoFactorAuth',
      'Help'
    ]),
    sub_admin: new Set([
      'EmployerDashboard',
      'CompanySettings',
      'EmployerEmployees',
      'EmployerEnrollments',
      'ClaimsManagement',
      'QualifyingLifeEvents',
      'PayrollReports',
      'DemographicReports',
      'TaxSavingsReports',
      'PenetrationReports',
      'CensusUpload',
      'ExportCensus',
      'BenefitsManagement',
      'Categories',
      'EnrollmentPeriods',
      'HRManagers',
      'TwoFactorAuth',
      'Help'
    ])
  };

  return allowed[userRole]?.has(pageName) || false;
};

export default function Layout({ children, currentPageName }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [vapiConfig, setVapiConfig] = useState(null);

  const { isDark } = useTheme();
  
  // Get current page context
  const pageContext = usePageContext();

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      // Check if user is authenticated
      const isAuth = await api.auth.isAuthenticated();
      
      if (!isAuth) {
        // Redirect to welcome for protected pages
        if (!publicPages.has(currentPageName)) {
          window.location.href = '/';
          return;
        }
      } else {
        const currentUser = await api.auth.me();
        
        // Route based on role and onboarding status
        if (currentUser.user_role === 'employee') {
          // For employees without employee_id, check if they have a linked employee record
          if (!currentUser.employee_id) {
            const employees = await api.entities.Employee.filter({ user_email: currentUser.email });
            
            if (employees.length === 0) {
              // No employee record - shouldn't happen, but redirect to role selection
              if (currentPageName !== 'RoleSelection') {
                window.location.href = '/RoleSelection';
                return;
              }
            } else {
              // Check if employee has an employer_id (linked to company)
              if (!employees[0].employer_id) {
                // Employee exists but not linked to employer - lock to onboarding/invite flow
                if (currentPageName !== 'EmployeeOnboarding' && currentPageName !== 'InviteCodeEntry') {
                  // Check if basic info is filled
                  if (!employees[0].date_of_birth) {
                    window.location.href = '/EmployeeOnboarding';
                  } else {
                    window.location.href = '/InviteCodeEntry';
                  }
                  return;
                }
              } else {
                // Employee linked to employer - update user and proceed
                await api.auth.updateMe({
                  employer_id: employees[0].employer_id,
                  employee_id: employees[0].id,
                  onboarding_completed: true
                });
                currentUser.employer_id = employees[0].employer_id;
                currentUser.employee_id = employees[0].id;
              }
            }
          }
        } else if (currentUser.user_role === 'employer_hr') {
          // For employer HR without employer_id, needs company setup
          if (!currentUser.employer_id && currentPageName !== 'CompanyOnboarding') {
            window.location.href = '/CompanyOnboarding';
            return;
          }
        }
        
        setUser(currentUser);
        
        // Load VAPI config for floating assistant
        try {
          const configs = await api.entities.SystemConfig.filter({
            config_key: { in: ['vapi_public_key', 'vapi_agent_id'] }
          });

          const configMap = {};
          configs.forEach(c => {
            const rawValue = c.config_value || c.value || c.configValue;
            const actualValue = typeof rawValue === 'object' && rawValue?.value ? rawValue.value : rawValue;
            configMap[c.config_key] = actualValue;
          });

          if (configMap.vapi_public_key && configMap.vapi_agent_id) {
            setVapiConfig(configMap);
          }
        } catch (err) {
          console.error('Failed to load VAPI config:', err);
        }
      }
    } catch (err) {
      console.error('Error loading user:', err);
      if (!publicPages.has(currentPageName)) {
        window.location.href = '/';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.auth.logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      // Still redirect even if API call fails
      window.location.href = '/';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          <p className="text-muted-foreground font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // For public pages or if no user, render without layout
  if (publicPages.has(currentPageName) || !user) {
    return <div className="min-h-screen bg-background text-foreground">{children}</div>;
  }

  const userRole = normalizeUserRole(user) || 'employee';

  if (!isSystemAdmin(user) && !isPageAllowedForRole(userRole, currentPageName)) {
    window.location.href = `/${getDefaultPageForRole(userRole)}`;
    return null;
  }

  return (
    <EnrollmentCartProvider>
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar
          userRole={userRole}
          currentPage={currentPageName}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          onLogout={handleLogout}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setMobileMenuOpen(false)}
          />
          <Sidebar
            userRole={userRole}
            currentPage={currentPageName}
            collapsed={false}
            onToggle={() => setMobileMenuOpen(false)}
            onLogout={handleLogout}
          />
        </div>
      )}

      {/* Main Content */}
      <div className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "lg:ml-20" : "lg:ml-64"
      )}>
        <TopBar 
          user={user} 
          onMenuToggle={() => setMobileMenuOpen(true)}
          showMenuButton={true}
        />
        <main className="p-4 sm:p-6">
          {children}
        </main>
      </div>

      {/* Floating Benefits Assistant */}
      {vapiConfig && (
        <VapiWidget
          publicKey={vapiConfig.vapi_public_key}
          assistantId={vapiConfig.vapi_agent_id}
          mode="hybrid"
          position="bottom-right"
          theme={isDark ? 'dark' : 'light'}
          accentColor="#14B8A6"
          ctaButtonColor="#059669"
          ctaButtonTextColor="#ffffff"
          borderRadius="large"
          size="compact"
          title="Benefits Assistant"
          ctaTitle="Need Help?"
          ctaSubtitle="Ask me anything"
          chatFirstMessage={`Hi! I'm your benefits assistant${pageContext ? ` on the ${pageContext.title} page` : ''}. How can I help you today?`}
          chatPlaceholder="Type your question..."
          voiceShowTranscript={true}
          assistantOverrides={{
            variableValues: {
              currentPage: pageContext?.title || 'Unknown',
              pageDescription: pageContext?.description || '',
              pageCapabilities: pageContext?.capabilities?.join(', ') || '',
              helpTopics: pageContext?.helpTopics?.join(', ') || ''
            }
          }}
        />
      )}
    </div>
    </EnrollmentCartProvider>
  );
}